import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Layout, Badge, Grid, Dropdown, Avatar, Input } from 'antd';
import {
  DownOutlined,
  EnvironmentFilled,
  UserOutlined,
  UserSwitchOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  LogoutOutlined,
  HomeOutlined,
  AppstoreOutlined,
  ReadOutlined,
  MenuOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useActiveCart } from '@core/hooks/useCart';
import { useWishlistStore } from '@core/store/wishlistStore';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { getAvatarStyles } from '@core/utils/avatar';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useUiStore } from '@core/store/uiStore';
import CartDrawer from '@components/base/CartDrawer';
import CheckoutModal from '@components/base/CheckoutModal';
import {
  CHECKOUT_MODAL_REDIRECT,
  loadCheckoutModule,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import ImageWithFallback from '@components/base/ImageWithFallback';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import Price from '@components/base/Price';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useProfileDetails } from '@/features/account/AccountPage';

const { Header } = Layout;
const CURRENT_LOCATION_ID = 'temp-current-location';
const fallbackRestaurantLocations = [
  { id: 'temp-restaurant-chennai', name: 'Chennai', city: 'Chennai', state: 'Tamil Nadu' },
  { id: 'temp-restaurant-coimbatore', name: 'Coimbatore', city: 'Coimbatore', state: 'Tamil Nadu' },
  { id: 'temp-restaurant-madurai', name: 'Madurai', city: 'Madurai', state: 'Tamil Nadu' },
];

// Editorial type pair for the restaurant skin; injected once, restaurant only.
const EDITORIAL_FONTS_ID = 'restaurant-editorial-fonts';
const EDITORIAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500;1,600;1,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';

function useEditorialFonts() {
  useEffect(() => {
    if (document.getElementById(EDITORIAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = EDITORIAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = EDITORIAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

const fallbackNav = [
  { label: 'Home', to: '/' },
  { label: 'Menu', to: '/catalog' },
  { label: 'Categories', to: '/catalog?view=categories' },
  { label: 'My Orders', to: '/orders' },
];

export default function HeaderRestaurant() {
  const t = useCopy();
  useEditorialFonts();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { customer, isAuthenticated, logout } = useAuth();
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const { cart, lineCount } = useActiveCart();
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const showMobileTabs = !screens.sm;
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { displayName } = useProfileDetails();

  const [searchText, setSearchText] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const isHomePage = pathname === '/';

  // Home page (desktop): the header floats transparently over the hero, like
  // the coffee skin, and turns solid + fixed once the page scrolls.
  // The page may scroll inside a wrapper element rather than the window, so
  // remember whichever element scrolls vertically; horizontal scrollers (e.g.
  // the autoplaying category rail) report scrollTop 0 and must be ignored.
  useEffect(() => {
    let verticalScroller = null;
    const handleScroll = (event) => {
      const target = event?.target;
      if (target instanceof Element && target.scrollTop > 0) verticalScroller = target;
      const scrollTop = Math.max(
        window.scrollY || 0,
        document.documentElement?.scrollTop || 0,
        document.body?.scrollTop || 0,
        verticalScroller?.scrollTop || 0,
      );
      setIsScrolled(scrollTop > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, []);
  const [isDesktopSearchOpen, setIsDesktopSearchOpen] = useState(false);
  const desktopSearchRef = useRef(null);

  useEffect(() => {
    if (isDesktopSearchOpen) desktopSearchRef.current?.focus();
  }, [isDesktopSearchOpen]);
  const searchTimeoutRef = useRef(null);

  const isSearchAllowedPage =
    pathname === '/' ||
    pathname === '/catalog' ||
    pathname === '/categories' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/categories');
  const shouldHideHeaderSearch = !isSearchAllowedPage;
  // The desktop menu page has its own search box; the mobile one doesn't.
  const isMenuPage = pathname.startsWith('/catalog');

  const onSearch = (value) => {
    if (shouldHideHeaderSearch) return;
    const q = value.trim();
    navigate(q ? `/catalog?q=${encodeURIComponent(q)}` : '/catalog');
  };

  useEffect(() => {
    const query = new URLSearchParams(search).get('q') || '';
    setSearchText(query);
  }, [search]);

  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);

  const selectableLocations = locations.length ? locations : fallbackRestaurantLocations;
  const selectedLocation =
    locationId === CURRENT_LOCATION_ID
      ? { id: CURRENT_LOCATION_ID, name: 'Current location', city: 'Current location' }
      : selectableLocations.find((loc) => loc.id === locationId);
  const activeLocation = selectedLocation ?? selectableLocations[0];
  const selectedLocationLabel =
    activeLocation?.name ||
    [activeLocation?.city, activeLocation?.state].filter(Boolean).join(', ') ||
    '';
  const hasSelectedMobileLocation = Boolean(selectedLocation);
  const mobileLocationLabel =
    selectedLocation?.name ||
    [selectedLocation?.city, selectedLocation?.state].filter(Boolean).join(', ');
  const navItems = useMemo(() => {
    const configured = config?.nav?.primary ?? [];
    const merged = configured.length ? configured : [{ label: 'Menu', to: '/catalog' }];
    const deduped = new Map();
    merged.forEach((item) => {
      if (!deduped.has(item.to)) deduped.set(item.to, item);
    });
    return [...deduped.values()].length ? [...deduped.values()] : fallbackNav;
  }, [config?.nav?.primary]);

  const mobileTabs = [
    { key: 'home', to: '/', label: t('home', 'Home'), icon: <HomeOutlined /> },
    {
      key: 'shop',
      to: '/catalog',
      label: t('menu', 'Menu'),
      icon: <MenuOutlined />,
    },
    {
      key: 'categories',
      to: '/categories',
      label: t('categories', 'Categories'),
      icon: <AppstoreOutlined />,
    },
    { key: 'orders', to: '/orders', label: t('orders', 'Orders'), icon: <ReadOutlined /> },
  ];

  const isTabActive = (tab) => {
    const searchParams = new URLSearchParams(search);
    const isCategoryView = searchParams.get('view') === 'categories';
    if (tab.key === 'home') return pathname === '/';
    if (tab.key === 'shop') return pathname.startsWith('/catalog') && !isCategoryView;
    if (tab.key === 'favorites') return pathname.startsWith('/wishlist');
    return pathname.startsWith(tab.to);
  };

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName, disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    {
      key: 'switch_account',
      icon: <UserSwitchOutlined />,
      label: t('switchAccount', 'Switch Account'),
    },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'orders') navigate('/orders');
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  const onOrdersClick = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };

  useEffect(() => {
    if (!location.state?.openCartDrawer) return;
    setCartOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  const brandLogo = runtimeLogoUrl || config?.logoUrl;
  const brandLabel = runtimeBrandName || t('appName', config?.name ?? 'Our Kitchen');
  const renderSearchInput = (extraProps = {}) => (
    <Input
      placeholder={t('searchTheMenu', 'Search the menu')}
      value={searchText}
      onChange={(event) => {
        const value = event.target.value;
        setSearchText(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
          onSearch(value);
        }, 300);
      }}
      onPressEnter={() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        onSearch(searchText);
      }}
      prefix={<SearchOutlined aria-hidden />}
      aria-label="Search the menu"
      allowClear
      variant="borderless"
      className="rx-mast__search-input"
      {...extraProps}
    />
  );

  // Desktop search collapses to an icon; clicking it expands the field. It
  // folds back when it loses focus while empty, or on Escape.
  const desktopSearch = isDesktopSearchOpen ? (
    renderSearchInput({
      ref: desktopSearchRef,
      onBlur: () => {
        if (!searchText.trim()) setIsDesktopSearchOpen(false);
      },
      onKeyDown: (event) => {
        if (event.key !== 'Escape') return;
        setIsDesktopSearchOpen(false);
        event.currentTarget.blur();
      },
    })
  ) : (
    <button
      type="button"
      className="rx-mast__icon"
      aria-label={t('search', 'Search')}
      aria-expanded="false"
      onClick={() => setIsDesktopSearchOpen(true)}
    >
      <SearchOutlined />
    </button>
  );

  const isNavActive = (item) => {
    const [itemPath, itemQuery = ''] = item.to.split('?');
    const isCategoryView = new URLSearchParams(search).get('view') === 'categories';
    if (itemPath === '/') return pathname === '/';
    if (itemQuery.includes('view=categories')) {
      return pathname.startsWith('/categories') || (pathname === '/catalog' && isCategoryView);
    }
    if (itemPath === '/catalog') return pathname.startsWith('/catalog') && !isCategoryView;
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  };

  const brandMark = (maxHeight, maxWidth) =>
    brandLogo ? (
      <ImageWithFallback
        src={brandLogo}
        alt={brandLabel}
        fit="contain"
        radius="0"
        style={{ maxHeight, maxWidth, background: 'transparent' }}
      />
    ) : (
      <span className="rx-mast__monogram" aria-hidden="true">
        {brandLabel.charAt(0)}
      </span>
    );

  const closeCart = useCallback(() => setCartOpen(false), []);
  // Checkout opens in a modal over the page (coffee flow); after a login it
  // reopens there instead of routing to /checkout.
  const goToCheckout = () => {
    if (isAuthenticated) {
      openCheckoutModal();
      return;
    }
    setAuthRedirect(CHECKOUT_MODAL_REDIRECT);
    setAuthMode('login');
  };

  // Fetch the checkout code while the cart is open so the modal opens fast.
  useEffect(() => {
    if (cartOpen) loadCheckoutModule();
  }, [cartOpen]);

  return (
    <>
      <Header
        className={`rx-mast ${isMobile ? 'is-mobile' : ''} ${
          isHomePage && !isMobile ? 'rx-mast--hero' : ''
        } ${isScrolled ? 'is-scrolled' : ''}`}
      >
        {!isMobile ? (
          <div className="rx-mast__inner">
            <div className="rx-mast__left">
              <Link to="/" className="rx-mast__brand" aria-label={t('home', 'Home')}>
                {brandMark(36, 56)}
                <span className="rx-mast__brand-text">
                  <strong>{brandLabel}</strong>
                  {config?.copy?.brandSubline ? (
                    <small>{t('brandSubline', config.copy.brandSubline)}</small>
                  ) : null}
                </span>
              </Link>
              <button
                type="button"
                className="rx-mast__outlet"
                aria-label={t('changeStore', 'Change store')}
                onClick={openStoreSelector}
              >
                <EnvironmentFilled aria-hidden className="rx-mast__outlet-icon" />
                <span className="rx-mast__outlet-text">
                  {selectedLocation ? selectedLocationLabel : ''}
                </span>
                <DownOutlined aria-hidden className="rx-mast__outlet-caret" />
              </button>
            </div>

            <nav className="rx-mast__nav" aria-label="Primary">
              {navItems.map((item) => {
                const active = isNavActive(item);
                const label = t(item.label.toLowerCase().replace(/\s+/g, ''), item.label);
                if (item.to === '/orders') {
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={active ? 'is-active' : undefined}
                      aria-current={active ? 'page' : undefined}
                      onClick={(event) => {
                        if (isAuthenticated) return;
                        event.preventDefault();
                        onOrdersClick();
                      }}
                    >
                      {label}
                    </Link>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={active ? 'is-active' : undefined}
                    aria-current={active ? 'page' : undefined}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>

            <div className="rx-mast__actions">
              {!shouldHideHeaderSearch && !isMenuPage ? (
                <div className={`rx-mast__search ${isDesktopSearchOpen ? 'is-open' : ''}`}>
                  {desktopSearch}
                </div>
              ) : null}
              <Badge count={wishlistCount} size="small" offset={[-4, 4]}>
                <Link to="/wishlist" className="rx-mast__icon" aria-label="Wishlist">
                  <HeartOutlined />
                </Link>
              </Badge>
              <button
                type="button"
                className="rx-mast__cart"
                aria-label={t('cart', 'Cart')}
                onClick={() => setCartOpen(true)}
              >
                <ShoppingCartOutlined aria-hidden />
                <Price amount={cart?.grandTotal ?? 0} className="rx-mast__cart-total" />
                <em>{lineCount}</em>
              </button>
              {isAuthenticated ? (
                <Dropdown
                  menu={{ items: accountItems, onClick: onAccountClick }}
                  trigger={['click']}
                >
                  <button type="button" className="rx-mast__account" aria-label="Account menu">
                    <span>{t('account', 'Account')}</span>
                    <Avatar size={30} style={getAvatarStyles(displayName)}>
                      {displayName[0]?.toUpperCase()}
                    </Avatar>
                  </button>
                </Dropdown>
              ) : (
                <button
                  type="button"
                  className="rx-mast__account"
                  onClick={() => {
                    setAuthRedirect('/account');
                    setAuthMode('login');
                  }}
                >
                  <span>{t('login', 'Login')}</span>
                  <span className="rx-mast__account-icon" aria-hidden="true">
                    <UserOutlined />
                  </span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="rx-mast__mobile">
              <Link to="/" className="rx-mast__brand rx-mast__brand--mobile" aria-label="Home">
                {brandMark(36, 96)}
              </Link>

              <button
                type="button"
                className={`rx-mast__outlet rx-mast__outlet--mobile ${
                  hasSelectedMobileLocation ? 'is-selected' : ''
                }`}
                aria-label={t('changeStore', 'Change store')}
                onClick={openStoreSelector}
              >
                <EnvironmentFilled aria-hidden />
                <span>{mobileLocationLabel || ''}</span>
              </button>

              {!shouldHideHeaderSearch ? (
                <button
                  type="button"
                  className={`rx-mast__icon ${mobileSearchOpen ? 'is-active' : ''}`}
                  onClick={() => setMobileSearchOpen((prev) => !prev)}
                  aria-label="Search"
                  aria-expanded={mobileSearchOpen}
                >
                  <SearchOutlined />
                </button>
              ) : null}
              <Badge count={wishlistCount} size="small" offset={[-4, 10]}>
                <Link to="/wishlist" className="rx-mast__icon" aria-label="Wishlist">
                  <HeartOutlined />
                </Link>
              </Badge>

              {isAuthenticated ? (
                <Dropdown
                  menu={{ items: accountItems, onClick: onAccountClick }}
                  trigger={['click']}
                >
                  <button type="button" className="rx-mast__icon" aria-label="Account menu">
                    <Avatar size={30} style={getAvatarStyles(displayName)}>
                      {displayName[0]?.toUpperCase()}
                    </Avatar>
                  </button>
                </Dropdown>
              ) : (
                <button
                  type="button"
                  className="rx-mast__icon"
                  onClick={() => {
                    setAuthRedirect('/account');
                    setAuthMode('login');
                  }}
                  aria-label="Login"
                >
                  <UserOutlined />
                </button>
              )}
            </div>
            {mobileSearchOpen && !shouldHideHeaderSearch ? (
              <div className="rx-mast__mobile-search">{renderSearchInput({ autoFocus: true })}</div>
            ) : null}
          </>
        )}
      </Header>

      {showMobileTabs ? (
        <nav className="restaurant-mobile-tabs" aria-label="Mobile navigation">
          {mobileTabs.map((tab) => (
            <Link
              key={tab.key}
              to={tab.to}
              onClick={(e) => {
                if (tab.onClick) {
                  e.preventDefault();
                  tab.onClick();
                } else if (tab.to === '/orders' && !isAuthenticated) {
                  e.preventDefault();
                  onOrdersClick();
                }
              }}
              className={`restaurant-mobile-tab ${isTabActive(tab) ? 'active' : ''}`}
            >
              <span className="restaurant-mobile-tab-icon">{tab.icon}</span>
              <span className="restaurant-mobile-tab-label">{tab.label}</span>
            </Link>
          ))}
        </nav>
      ) : null}

      <CartDrawer open={cartOpen} onClose={closeCart} onCheckout={goToCheckout} />
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === CHECKOUT_MODAL_REDIRECT) openCheckoutModal();
          else if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
      <CheckoutModal className="restaurant-checkout-modal" />
    </>
  );
}
